import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Input } from "@/components/ui/input";
import { MEASURE_BANK, SCORECARD_MAX, SCORECARD_MIN, scoreReport, type ScoreInputs } from "@/lib/tools/calc";
import { ToolPageShell, toolHead } from "@/components/site/tools/ToolPageShell";
import { ScoreGrid } from "@/components/site/tools/ToolCharts";

export const Route = createFileRoute("/tools/weekly-scorecard-builder")({
  head: () => toolHead("weekly-scorecard-builder"),
  component: ScorecardPage,
});

type Item = { name: string; group: string; selected: boolean; owner: string; target: string };

const seed: Item[] = MEASURE_BANK.flatMap((g) => g.measures.map((name) => ({ name, group: g.group, selected: false, owner: "", target: "" })));
const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function ScorecardPage() {
  const [items, setItems] = useState<Item[]>(seed);
  const [custom, setCustom] = useState("");
  const chosen = items.filter((m) => m.selected);
  const inputs: ScoreInputs = { measures: chosen.map(({ name, group, owner, target }) => ({ name, group, owner: owner.trim(), target: target.trim() })) };
  const full = chosen.length >= SCORECARD_MAX;
  const update = (name: string, patch: Partial<Item>) => setItems((p) => p.map((m) => (m.name === name ? { ...m, ...patch } : m)));
  const addCustom = () => {
    const name = custom.trim().slice(0, 80);
    if (!name || items.some((m) => m.name === name)) return;
    setItems((p) => [...p, { name, group: "Your own", selected: !full, owner: "", target: "" }]);
    setCustom("");
  };
  const groups = [...MEASURE_BANK.map((g) => g.group), ...(items.some((m) => m.group === "Your own") ? ["Your own"] : [])];

  return (
    <ToolPageShell
      slug="weekly-scorecard-builder"
      report={scoreReport(inputs)}
      inputs={inputs}
      chart={<ScoreGrid inputs={inputs} />}
      form={
        <div>
          <p className="text-sm text-charcoal">Choose {SCORECARD_MIN} to {SCORECARD_MAX} measures. {chosen.length} chosen.</p>
          {groups.map((g) => (
            <fieldset key={g} className="mt-5">
              <legend className="font-semibold text-midnight">{g}</legend>
              <ul className="mt-2 space-y-2">
                {items.filter((m) => m.group === g).map((m) => {
                  const id = slug(m.name);
                  return (
                    <li key={m.name} className="rounded-xl border border-border bg-surface p-3">
                      <label className="flex items-center gap-3 text-sm font-medium">
                        <input type="checkbox" checked={m.selected} disabled={!m.selected && full} onChange={(e) => update(m.name, { selected: e.target.checked })} className="h-4 w-4 accent-midnight" />
                        {m.name}
                      </label>
                      {m.selected ? (
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                          <div>
                            <label htmlFor={`${id}-o`} className="text-xs font-medium text-charcoal">Owner</label>
                            <Input id={`${id}-o`} value={m.owner} maxLength={60} placeholder="One named person" onChange={(e) => update(m.name, { owner: e.target.value })} className="mt-1 h-11 border-field-border bg-card" />
                          </div>
                          <div>
                            <label htmlFor={`${id}-t`} className="text-xs font-medium text-charcoal">Weekly target</label>
                            <Input id={`${id}-t`} value={m.target} maxLength={40} onChange={(e) => update(m.name, { target: e.target.value })} className="mt-1 h-11 border-field-border bg-card" />
                          </div>
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </fieldset>
          ))}
          <div className="mt-6 border-t border-border pt-5">
            <label htmlFor="sc-custom" className="text-sm font-medium text-foreground">Add your own measure</label>
            <div className="mt-1 flex gap-2">
              <Input id="sc-custom" value={custom} maxLength={80} onChange={(e) => setCustom(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }} className="h-11 border-field-border bg-card" />
              <button type="button" onClick={addCustom} className="min-h-11 shrink-0 rounded-full border border-midnight px-5 text-sm font-semibold text-midnight hover:bg-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">Add</button>
            </div>
          </div>
        </div>
      }
    />
  );
}
